import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';
import { Download, Calendar, ChevronRight, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { MoonLoader } from './ui/moon-loader';
import { TranslatedText } from './translated-text';
import apiService from '../lib/api';
import { categoriesApi } from '../lib/api/categories';
import { foodApi } from '../lib/api/food';
import { getCompanyId } from '../lib/config';
import { formatCurrency } from '../lib/utils'; // Assuming this utility exists, otherwise defined locally

// Types
interface TransactionDetail {
  invoiceNumber: string;
  time: string;
  date: string; // Added date to transaction detail since it's now a range report
  quantity: number;
  total: number;
}

interface ReportItem {
  id: string; // Use name as ID if actual ID missing
  name: string;
  quantity: number;
  total: number;
  averagePrice: number;
  transactions: TransactionDetail[];
}

export function FoodReportsTab() {
  const companyId = getCompanyId();
  
  // Date State
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  
  // Selection State
  const [selectedItem, setSelectedItem] = useState<ReportItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: keyof ReportItem, direction: 'asc' | 'desc' }>({ key: 'total', direction: 'desc' });

  // Fetch Invoices
  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['invoices-report', companyId],
    queryFn: () => apiService.getInvoices({ companyId, pageSize: 1000 }), 
    enabled: !!companyId
  });

  // Fetch Purchase Orders for Kitchen Items
  const { data: kitchenItemsData, isLoading: isKitchenLoading } = useQuery({
    queryKey: ['kitchen-items-report', companyId, startDate, endDate],
    queryFn: async () => {
      if (!companyId) return { items: [], pagination: null };
      try {
        // Import kitchen items API
        const { kitchenItemsApi } = await import('../lib/api/accounting');
        // Fetch all kitchen items (use large page size to get all)
        const response = await kitchenItemsApi.getKitchenItems(companyId, 1, 1000);
        return response;
      } catch (e) {
        console.error("Failed to fetch kitchen items", e);
        return { items: [], pagination: null };
      }
    },
    enabled: !!companyId
  });

  // Extract items if paginated
  const invoiceList = Array.isArray(invoicesData) ? invoicesData : ((invoicesData as any)?.items || []);

  // Fetch Food Categories - MATCHING LOGIC FROM INVENTORY TAB
  const { data: foodCategoryIds = new Set<string>() } = useQuery({
    queryKey: ['food-category-ids-report', companyId],
    queryFn: async () => {
      if (!companyId) return new Set<string>();
      try {
        const response = await categoriesApi.getCategories(companyId);
        const all: any[] = Array.isArray(response) ? response : (response as any).items || [];
        
        // 1. Find the "Food" parent category
        const foodKeywords = ['food', 'beverage', 'kitchen', 'restaurant', 'menu'];
        const foodParent = all.find((c: any) => 
          !c.parentId && foodKeywords.some(k => c.name.toLowerCase().includes(k))
        );

        const ids = new Set<string>();

        if (foodParent) {
            ids.add(foodParent.id);
            all.filter((c: any) => c.parentId === foodParent.id).forEach(c => ids.add(c.id));
        } else {
            const fallbackKeywords = ['food', 'beverage', 'drink', 'kitchen', 'snack', 'bar', 'breakfast', 'lunch', 'dinner'];
            all.filter(c => fallbackKeywords.some(k => c.name.toLowerCase().includes(k)))
               .forEach(c => ids.add(c.id));
        }

        return ids;
      } catch (e) {
        console.error("Failed to fetch categories for report filter", e);
        return new Set<string>();
      }
    },
    enabled: !!companyId
  });

  // Fetch Food Items to build valid ID set
  const { data: foodItemsData = { ids: new Set<string>(), names: new Set<string>() } } = useQuery({
    queryKey: ['food-items-filter-data', companyId, foodCategoryIds.size], 
    queryFn: async () => {
      if (!companyId) return { ids: new Set<string>(), names: new Set<string>() };
      try {
        const items = await foodApi.getFoodItems(undefined, companyId);
        const ids = new Set<string>();
        const names = new Set<string>();
        
        const list = Array.isArray(items) ? items : (items as any).items || [];
        
        list.forEach((i: any) => {
          if (foodCategoryIds.size > 0) {
              if (i.categoryId && foodCategoryIds.has(i.categoryId)) {
                if (i.id) ids.add(i.id);
                if (i.name) names.add(i.name.toLowerCase().trim());
              }
          }
        });
        
        return { ids, names };
      } catch (e) {
        console.error("Failed to fetch food items for filter", e);
        return { ids: new Set<string>(), names: new Set<string>() };
      }
    },
    enabled: !!companyId
  });

  // Aggregate Data - GROUP BY ITEM
  const aggregatedItems = useMemo(() => {
    if (!invoiceList?.length) return [];

    const itemsMap = new Map<string, ReportItem>();

    invoiceList.forEach((inv: any) => {
      // Filter by date range
      const invDate = parseISO(inv.issueDate);
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      end.setHours(23, 59, 59, 999);

      if (!isWithinInterval(invDate, { start, end })) return;
      if (inv.status !== 'paid') return; 

      if (!inv.lines || inv.lines.length === 0) return;

      // Process lines individually
      inv.lines.forEach((line: any) => {
        const description = line.description?.toLowerCase().trim() || '';
        const rawName = line.description?.trim() || 'Unknown Item';
        
        // STRICT FILTER
        const isFood = 
          (line.foodItemId && foodItemsData.ids.has(line.foodItemId)) ||
          (line.productId && foodItemsData.ids.has(line.productId)) ||
          (description && foodItemsData.names.has(description)) ||
          (line.product && line.product.categoryId && foodCategoryIds.has(line.product.categoryId));

        if (!isFood) return; 

        // Use name as key to aggregate distinct entries of same item
        const key = rawName; 

        if (!itemsMap.has(key)) {
          itemsMap.set(key, { 
            id: key, 
            name: rawName, 
            quantity: 0, 
            total: 0, 
            averagePrice: 0,
            transactions: [] 
          });
        }

        const itemStat = itemsMap.get(key)!;
        const lineTotal = (Number(line.unitPrice) * Number(line.quantity)) - (Number(line.lineDiscount) || 0);

        itemStat.quantity += Number(line.quantity);
        itemStat.total += lineTotal;
        itemStat.transactions.push({
           invoiceNumber: inv.invoiceNumber,
           time: format(invDate, 'HH:mm'),
           date: format(invDate, 'dd MMM yyyy'),
           quantity: Number(line.quantity),
           total: lineTotal
        });
      });
    });

    // Calculate averages and return array
    const results = Array.from(itemsMap.values()).map(item => ({
      ...item,
      averagePrice: item.quantity > 0 ? item.total / item.quantity : 0
    }));

    // Sort
    return results.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      // @ts-ignore
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

  }, [invoicesData, startDate, endDate, foodItemsData, foodCategoryIds, sortConfig]);

  const grandTotal = useMemo(() => {
    return aggregatedItems.reduce((sum, item) => sum + item.total, 0);
  }, [aggregatedItems]);

  const totalQuantity = useMemo(() => {
    return aggregatedItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [aggregatedItems]);

  // Calculate Kitchen Purchase Orders Total
  const kitchenPurchaseTotal = useMemo(() => {
    const kitchenItems = kitchenItemsData?.items || [];
    
    if (!kitchenItems.length) {
      console.log('📦 No kitchen items data');
      return 0;
    }

    console.log('📦 Kitchen Items Data:', kitchenItems.length, 'items');
    console.log('📦 Date Range:', startDate, 'to', endDate);

    let total = 0;
    let includedItems = 0;
    let skippedByDate = 0;

    kitchenItems.forEach((item: any) => {
      // Filter by date range
      const itemDate = parseISO(item.date);
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      end.setHours(23, 59, 59, 999);

      if (!isWithinInterval(itemDate, { start, end })) {
        skippedByDate++;
        return;
      }
      
      console.log(`📦 Kitchen Item ${item.poNumber} - Date: ${format(itemDate, 'yyyy-MM-dd')}, Amount: ${item.amount}`);
      
      // Add the amount of this kitchen item
      const itemAmount = Number(item.amount) || 0;
      total += itemAmount;
      includedItems++;
      console.log(`  ✅ Included! Amount: ${itemAmount}`);
    });

    console.log('📦 Summary:');
    console.log(`  - Total Kitchen Items: ${kitchenItems.length}`);
    console.log(`  - Skipped by date: ${skippedByDate}`);
    console.log(`  - Included Items: ${includedItems}`);
    console.log(`  - Total Cost: ${total}`);

    return total;
  }, [kitchenItemsData, startDate, endDate]);

  // Calculate Profit (Revenue - Purchase Cost)
  const profit = useMemo(() => {
    return grandTotal - kitchenPurchaseTotal;
  }, [grandTotal, kitchenPurchaseTotal]);

  const profitMargin = useMemo(() => {
    if (grandTotal === 0) return 0;
    return (profit / grandTotal) * 100;
  }, [profit, grandTotal]);

  const handleSort = (key: keyof ReportItem) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Handle Download (CSV)
  const handleDownload = () => {
    const headers = ['Item Name', 'Total Quantity', 'Average Price', 'Total Sales'];
    const rows = [];

    rows.push([`Kitchen Sales Report: ${startDate} to ${endDate}`]);
    rows.push([`Grand Total: ${grandTotal}`]);
    rows.push([]);
    rows.push(headers);

    aggregatedItems.forEach(item => {
      rows.push([
        `"${item.name}"`,
        item.quantity,
        (item.total / item.quantity).toFixed(2),
        item.total
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," 
      + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `kitchen_report_${startDate}_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle><TranslatedText text="Kitchen Sales Reports" /></CardTitle>
            <Button variant="outline" onClick={handleDownload} disabled={!aggregatedItems.length}>
              <Download className="w-4 h-4 mr-2" />
              <TranslatedText text="Download CSV" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <label className="text-sm font-medium mb-1 block"><TranslatedText text="Start Date" /></label>
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                className="w-40"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block"><TranslatedText text="End Date" /></label>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                className="w-40"
              />
            </div>
            <div className="flex-1 flex justify-end gap-8">
               <div className="text-right">
                   <div className="text-sm text-muted-foreground"><TranslatedText text="Items Sold" /></div>
                   <div className="text-2xl font-bold text-slate-700">{totalQuantity}</div>
               </div>
               <div className="text-right">
                   <div className="text-sm text-muted-foreground"><TranslatedText text="Total Revenue" /></div>
                   <div className="text-2xl font-bold text-green-600">{formatCurrency(grandTotal || 0)}</div>
               </div>
               <div className="text-right">
                   <div className="text-sm text-muted-foreground"><TranslatedText text="Purchase Cost" /></div>
                   <div className="text-2xl font-bold text-orange-600">{formatCurrency(kitchenPurchaseTotal || 0)}</div>
               </div>
               <div className="text-right">
                   <div className="text-sm text-muted-foreground"><TranslatedText text="Profit" /></div>
                   <div className={`text-2xl font-bold ${profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                     {formatCurrency(profit || 0)}
                   </div>
                   <div className="text-xs text-muted-foreground mt-1">
                     {profitMargin.toFixed(1)}% margin
                   </div>
               </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <MoonLoader size="lg" />
            </div>
          ) : aggregatedItems.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center text-slate-400">
              <FileText className="w-16 h-16 mb-4 opacity-20" />
              <p className="text-lg"><TranslatedText text="No food sales found for this period" /></p>
              <p className="text-sm"><TranslatedText text="Try adjusting the date range" /></p>
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-[40%] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('name')}>
                    <div className="flex items-center gap-1">
                      <TranslatedText text="Item Name" /> 
                      {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('quantity')}>
                    <div className="flex items-center justify-end gap-1">
                      <TranslatedText text="Quantity" />
                      {sortConfig.key === 'quantity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('averagePrice')}>
                    <div className="flex items-center justify-end gap-1">
                       <TranslatedText text="Avg Price" />
                       {sortConfig.key === 'averagePrice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('total')}>
                    <div className="flex items-center justify-end gap-1">
                      <TranslatedText text="Total Revenue" />
                      {sortConfig.key === 'total' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aggregatedItems.map((item, idx) => (
                  <TableRow 
                    key={idx} 
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsDetailOpen(true);
                    }}
                  >
                    <TableCell className="font-medium text-slate-700">{item.name}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-white">{item.quantity}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-500">
                      {formatCurrency(item.averagePrice)}
                    </TableCell>
                    <TableCell className="text-right font-bold text-slate-800">
                      {formatCurrency(item.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Breakdown Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center pr-8">
               <span>{selectedItem?.name}</span>
               <Badge variant="secondary"><TranslatedText text="Sales History" /></Badge>
            </DialogTitle>
            <DialogDescription>
              {startDate} <TranslatedText text="to" /> {endDate} • {selectedItem?.quantity} <TranslatedText text="items sold" />
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[500px] overflow-y-auto mt-2">
             <Table>
               <TableHeader className="bg-slate-50 sticky top-0">
                 <TableRow>
                   <TableHead><TranslatedText text="Date" /></TableHead>
                   <TableHead><TranslatedText text="Time" /></TableHead>
                   <TableHead><TranslatedText text="Invoice" /></TableHead>
                   <TableHead className="text-right"><TranslatedText text="Qty" /></TableHead>
                   <TableHead className="text-right"><TranslatedText text="Amount" /></TableHead>
                 </TableRow>
               </TableHeader>
               <TableBody>
                 {selectedItem?.transactions.sort((a, b) => b.date.localeCompare(a.date)).map((tx, idx) => (
                   <TableRow key={idx}>
                     <TableCell className="text-sm">{tx.date}</TableCell>
                     <TableCell className="text-xs text-muted-foreground">{tx.time}</TableCell>
                     <TableCell className="font-mono text-xs text-blue-600">{tx.invoiceNumber}</TableCell>
                     <TableCell className="text-right">{tx.quantity}</TableCell>
                     <TableCell className="text-right font-medium">{formatCurrency(tx.total)}</TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
          </div>
          <div className="pt-4 border-t flex justify-between items-center bg-slate-50 p-4 rounded-lg mt-2">
             <span className="font-medium text-slate-600"><TranslatedText text="Total Revenue" /></span>
             <span className="font-bold text-xl text-green-600">{selectedItem ? formatCurrency(selectedItem.total) : ''}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
